import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveSurvey,
  listSurveys,
  type Survey,
  type SurveyStatus,
} from '../api/surveys.js';
import { listChannels } from '../api/channels.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const STATUS_TONE: Record<SurveyStatus, 'green' | 'yellow' | 'slate'> = {
  active: 'green',
  paused: 'yellow',
  archived: 'slate',
};

const STATUSES: SurveyStatus[] = ['active', 'paused', 'archived'];

export function SurveysList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState<SurveyStatus | ''>('');
  const [confirmTarget, setConfirmTarget] = useState<Survey | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  const channelsQuery = useQuery({
    queryKey: ['channels', id, { includeArchived: false }],
    queryFn: () => listChannels(id),
    enabled: Boolean(id),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['surveys', id, { includeArchived, status: statusFilter || undefined }],
    queryFn: () =>
      listSurveys(id, {
        includeArchived,
        status: statusFilter || undefined,
      }),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: (surveyId: string) => archiveSurvey(id, surveyId),
    onSuccess: () => {
      pushToast('success', t.survey.archived);
      queryClient.invalidateQueries({ queryKey: ['surveys', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const surveys = data ?? [];
  const channels = channelsQuery.data ?? [];
  const channelById = new Map(channels.map((c) => [c.id, c]));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.survey.listTitle}</h2>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600">
            <span className="sr-only">{t.survey.filterStatus}</span>
            <select
              aria-label={t.survey.filterStatus}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SurveyStatus | '')}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            >
              <option value="">{t.survey.filterAll}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t.survey.status[s]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.survey.showArchived}
          </label>
          <Link
            to={`/tenants/${id}/surveys/new`}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            {t.survey.newBtn}
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && surveys.length === 0 && (
        <p className="text-slate-500">{t.survey.empty}</p>
      )}

      {surveys.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">{t.survey.fields.name}</th>
                <th className="px-4 py-2">{t.survey.fields.flowId}</th>
                <th className="px-4 py-2">{t.survey.fields.channel}</th>
                <th className="px-4 py-2">{t.survey.fields.status}</th>
                <th className="px-4 py-2">{t.survey.fields.questionsCount}</th>
                <th className="px-4 py-2">{t.survey.fields.responsesCount}</th>
                <th className="px-4 py-2">{t.survey.fields.averageScore}</th>
                <th className="px-4 py-2">{t.survey.fields.version}</th>
                <th className="px-4 py-2 text-right">{t.survey.fields.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {surveys.map((sv) => {
                const channel = sv.channelId ? channelById.get(sv.channelId) : undefined;
                return (
                  <tr
                    key={sv.id}
                    data-testid={`survey-row-${sv.id}`}
                    className={sv.deletedAt ? 'opacity-50' : undefined}
                  >
                    <td className="px-4 py-2 font-medium">{sv.name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{sv.flowId}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {channel?.name ?? sv.channelId ?? '—'}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge
                        label={t.survey.status[sv.status]}
                        tone={STATUS_TONE[sv.status]}
                      />
                    </td>
                    <td className="px-4 py-2 text-slate-600">{sv.questionsCount}</td>
                    <td className="px-4 py-2 text-slate-600">{sv.responsesCount}</td>
                    <td className="px-4 py-2 text-slate-600">
                      {typeof sv.averageScore === 'number'
                        ? sv.averageScore.toFixed(2)
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-slate-500">v{sv.version}</td>
                    <td className="px-4 py-2 text-right">
                      {!sv.deletedAt && (
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/tenants/${id}/surveys/${sv.id}/edit`}
                            className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                          >
                            {t.survey.edit}
                          </Link>
                          <button
                            type="button"
                            onClick={() => setConfirmTarget(sv)}
                            className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                          >
                            {t.survey.archive}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title={t.survey.confirmArchiveTitle}
        description={t.survey.confirmArchiveDesc}
        confirmLabel={t.survey.archive}
        cancelLabel={t.app.cancel}
        loading={archiveMutation.isPending}
        onConfirm={() => {
          if (confirmTarget) archiveMutation.mutate(confirmTarget.id);
        }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
